import React, { useState, useCallback, useMemo } from 'react';
import Papa from 'papaparse';
import { saveAs } from 'file-saver';
import { Button, List, ListItem, Typography, Box, TextField, Divider, CircularProgress, Slider } from '@mui/material';
import { FileUploadArea } from 'app/components';
import { FileType } from 'app/components/FileUploadArea/FileUploadArea.types';
import { useIntl } from 'react-intl';

const MAX_FILES = 25;
const MAX_FILE_SIZE = 524288000; // 500 MB

const CsvSplitterPage = () => {
  const [fileChunks, setFileChunks] = useState<Array<{ blob: Blob; filename: string }>>([]);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [numberOfFiles, setNumberOfFiles] = useState<number>(3);
  const [sliderValue, setSliderValue] = useState<number>(3);
  const [numberOfFilesError, setNumberOfFilesError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const intl = useIntl();

  const handleFileUpload = useCallback((files: File[]) => {
    const file = files[0];
    if (!file) return;

    setUploadedFile(file);
    setFileChunks([]); // Clear previous results
  }, []);

  const handleSplitFiles = useCallback(() => {
    if (!uploadedFile) return;

    // Prevent processing if numberOfFiles is invalid
    if (numberOfFiles < 1 || numberOfFiles > MAX_FILES || isNaN(numberOfFiles)) {
      return;
    }

    setIsLoading(true);
    setFileChunks([]); // Clear previous results

    Papa.parse(uploadedFile, {
      header: true,
      complete: results => {
        const data = results.data;
        const header = results.meta.fields;
        const rowsPerFile = Math.ceil(data.length / numberOfFiles);

        const chunks: { blob: Blob; filename: string }[] = [];

        for (let i = 0; i < numberOfFiles && i * rowsPerFile < data.length; i++) {
          const chunk = data.slice(i * rowsPerFile, (i + 1) * rowsPerFile);
          const csvString = Papa.unparse(chunk, { columns: header, delimiter: ';' });
          const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
          chunks.push({
            blob,
            filename: `${uploadedFile.name.replace('.csv', '')}_part${i + 1}.csv`,
          });
        }

        setFileChunks(chunks);
        setIsLoading(false);
      },
    });
  }, [uploadedFile, numberOfFiles]);

  const downloadLink = useCallback((event: React.MouseEvent<HTMLAnchorElement>, blob: Blob, filename: string) => {
    event.preventDefault();
    saveAs(blob, filename);
  }, []);

  const downloadChunk = useCallback((blob: Blob, filename: string) => {
    saveAs(blob, filename);
  }, []);

  const handleNumberOfFilesChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      const numValue = parseInt(value);

      setNumberOfFiles(numValue);
      setSliderValue(numValue);

      if (value === '' || isNaN(numValue)) {
        setNumberOfFilesError(
          intl.formatMessage({ id: 'validationMessage.mixed.required' }, { default: 'Please enter a valid number' }),
        );
      } else if (numValue < 1 || numValue > MAX_FILES) {
        setNumberOfFilesError(
          intl.formatMessage(
            { id: 'tools.csvSplitter.error.numberOfSplits' },
            { default: 'The number of splits must be between 1 and 25.' },
          ),
        );
      } else {
        setNumberOfFilesError('');
      }
    },
    [intl],
  );

  const handleSliderChange = useCallback((event: Event, newValue: number | number[]) => {
    const value = newValue as number;
    setSliderValue(value);
    setNumberOfFiles(value);
    setNumberOfFilesError('');
  }, []);

  const downloadAllFiles = useCallback(() => {
    if (fileChunks.length === 0) return;

    fileChunks.forEach(({ blob, filename }, index) => {
      setTimeout(() => {
        saveAs(blob, filename);
      }, index * 200); // 200ms delay between downloads
    });
  }, [fileChunks]);

  const isProcessReady = useMemo(
    () => !!uploadedFile && !numberOfFilesError && numberOfFiles > 0 && numberOfFiles <= MAX_FILES && !isLoading,
    [uploadedFile, numberOfFilesError, numberOfFiles, isLoading],
  );

  const fileRangeText = useMemo(
    () => `(${1}-${MAX_FILES} ${intl.formatMessage({ id: 'connections.tab.files' })})`,
    [intl],
  );

  return (
    <div>
      <Typography variant="h5" gutterBottom sx={{ my: 2 }}>
        {intl.formatMessage({ id: 'tools.csvSplitter.description' })}
      </Typography>

      <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          {intl.formatMessage({ id: 'tools.csvSplitter.numberOfResultingFiles' })}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <TextField
            type="number"
            value={numberOfFiles}
            onChange={handleNumberOfFilesChange}
            error={!!numberOfFilesError}
            variant="outlined"
            disabled={isLoading}
            inputProps={{ style: { textAlign: 'center' } }}
            sx={{ textAlign: 'center' }}
          />
          <Slider
            value={sliderValue}
            onChange={handleSliderChange}
            min={1}
            max={MAX_FILES}
            step={1}
            disabled={isLoading}
            marks
            valueLabelDisplay="on"
            sx={{ flex: 1, mx: 2, maxWidth: 350 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90 }}>
            {fileRangeText}
          </Typography>
        </Box>
        {numberOfFilesError && (
          <Typography variant="body2" color="error" sx={{ ml: 1 }}>
            {numberOfFilesError}
          </Typography>
        )}
      </Box>

      <FileUploadArea
        maxSize={MAX_FILE_SIZE}
        onChange={handleFileUpload}
        types={[FileType.Csv]}
        filesLimit={1}
        showSuccessMessage={false}
        disabled={isLoading}
      />

      <Box sx={{ mt: 3, mb: 2 }}>
        <Button variant="contained" fullWidth size="large" onClick={handleSplitFiles} disabled={!isProcessReady}>
          {intl.formatMessage({ id: 'tools.csvSplitter.splitFiles' }, { default: 'Split files' })}
        </Button>
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4, gap: 5 }}>
          <CircularProgress size={50} />
          <Typography variant="h6">
            {intl.formatMessage({ id: 'state.processing' }, { default: 'Processing file...' })}
          </Typography>
        </Box>
      )}

      {fileChunks.length > 0 && !isLoading && (
        <>
          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            {intl.formatMessage({ id: 'tools.csvSplitter.yourFiles' })}
          </Typography>
          <List>
            {fileChunks.map(({ blob, filename }, index) => (
              <ListItem key={index} sx={{ display: 'flex', justifyContent: 'flex-start', gap: 5 }}>
                <Typography variant="h6" component={'a'} href="#" onClick={e => downloadLink(e, blob, filename)}>
                  {filename}
                </Typography>
                <Button variant="outlined" size="small" onClick={() => downloadChunk(blob, filename)}>
                  {intl.formatMessage({ id: 'global.download' })}
                </Button>
              </ListItem>
            ))}
          </List>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', justifyContent: 'center', pb: 5 }}>
            <Button variant="contained" onClick={downloadAllFiles} size="medium">
              {intl.formatMessage({ id: 'global.downloadAll' })}
            </Button>
          </Box>
        </>
      )}
    </div>
  );
};

export default CsvSplitterPage;
